import type { Skill, SkillRegistry, GetSkillInstructionResult, SkillSummary } from './types';
import { DOCUMENT_GENERATION_TOOL, REPAIR_DOCUMENT_JSON_TOOL } from '../lib/documentTool';
import { PRESENTATION_GENERATION_TOOL } from '../lib/presentationTool';
import { CAPTURE_SLIDES_TOOL, CREATE_THUMBNAIL_TOOL } from '../lib/presentationQATools';
import {
  UNPACK_PRESENTATION_TOOL,
  EXTRACT_SLIDE_TEXT_TOOL,
  PATCH_SLIDE_XML_TOOL,
  DUPLICATE_SLIDE_TOOL,
  DUPLICATE_PRESENTATION_TOOL,
  DELETE_SLIDE_TOOL,
  REORDER_SLIDES_TOOL,
  READ_PRESENTATION_IMAGE_TOOL,
  REPACK_PRESENTATION_TOOL,
} from '../lib/presentationEditingTools';
import {
  UNPACK_DOCUMENT_TOOL,
  READ_DOCUMENT_OUTLINE_TOOL,
  READ_DOCUMENT_TEXT_TOOL,
  INSPECT_DOCUMENT_PARAGRAPH_TOOL,
  REPLACE_DOCUMENT_TEXT_TOOL,
  REWRITE_DOCUMENT_PARAGRAPH_TOOL,
  MARK_DOCUMENT_FIELDS_DIRTY_TOOL,
  MOVE_DOCUMENT_SECTION_TOOL,
  DELETE_DOCUMENT_SECTION_TOOL,
  DELETE_DOCUMENT_BLOCKS_TOOL,
  RENAME_DOCUMENT_HEADING_TOOL,
  INSERT_DOCUMENT_PARAGRAPH_TOOL,
  APPLY_DOCUMENT_OPERATIONS_TOOL,
  READ_DOCUMENT_XML_TOOL,
  EDIT_DOCUMENT_XML_TOOL,
  PATCH_DOCUMENT_XML_TOOL,
  READ_DOCUMENT_IMAGE_TOOL,
  REPACK_DOCUMENT_TOOL,
} from '../lib/docxEditingTools';
import { PDF_EXTRACT_TEXT_TOOL, PDF_GET_METADATA_TOOL, PDF_OCR_TEXT_TOOL } from '../lib/pdfTool';
import { SPREADSHEET_GENERATION_TOOL, EDIT_SPREADSHEET_TOOL, DESCRIBE_SPREADSHEET_TOOL, SUMMARIZE_COLUMN_TOOL, QUERY_CELLS_TOOL, EXTRACT_SPREADSHEET_TEXT_TOOL, RUN_FORMULA_TOOL, FILTER_SPREADSHEET_TOOL } from '../lib/spreadsheetTool';

// Import skill instructions as raw strings
import documentSkillMd from '../../skills/document/SKILL.md?raw';
import documentEditingMd from '../../skills/document/editing.md?raw';
import presentationSkillMd from '../../skills/presentation/SKILL.md?raw';
import presentationCreatingMd from '../../skills/presentation/creating.md?raw';
import presentationGenerateMd from '../../skills/presentation/generate.md?raw';
import presentationEditingMd from '../../skills/presentation/editing.md?raw';
import presentationTemplateMd from '../../skills/presentation/template.md?raw';
import pdfSkillMd from '../../skills/pdf/SKILL.md?raw';
import spreadsheetSkillMd from '../../skills/spreadsheet/SKILL.md?raw';

/**
 * Map of additional files available for each skill.
 * Keys are filenames, values are the imported content.
 */
const skillFiles: Record<string, Record<string, string>> = {
  document: {
    'SKILL.md': documentSkillMd,
    'editing.md': documentEditingMd,
  },
  presentation: {
    'SKILL.md': presentationSkillMd,         // Routing table
    'generate.md': presentationGenerateMd,   // Design guidelines, rules, QA
    'creating.md': presentationCreatingMd,   // JSON spec & element reference
    'editing.md': presentationEditingMd,     // XML editing reference
    'template.md': presentationTemplateMd,   // Template-based generation workflow
  },
  pdf: {
    'SKILL.md': pdfSkillMd,
  },
  spreadsheet: {
    'SKILL.md': spreadsheetSkillMd,
  },
};

/**
 * Registry of all available skills.
 *
 * Each skill contains:
 * - name: Unique identifier
 * - description: What the skill does
 * - instructions: Detailed markdown instructions from the skill files
 * - tools: Claude API tool definitions
 * - triggers: Keywords that suggest this skill should be used
 */
export const skillRegistry: SkillRegistry = {
  document: {
    name: 'document',
    description: 'Create and edit Word documents (.docx) with structured content including headings, paragraphs, lists, and tables.',
    instructions: documentSkillMd,
    tools: [
      DOCUMENT_GENERATION_TOOL,
      REPAIR_DOCUMENT_JSON_TOOL,
      UNPACK_DOCUMENT_TOOL,
      READ_DOCUMENT_OUTLINE_TOOL,
      READ_DOCUMENT_TEXT_TOOL,
      INSPECT_DOCUMENT_PARAGRAPH_TOOL,
      REPLACE_DOCUMENT_TEXT_TOOL,
      REWRITE_DOCUMENT_PARAGRAPH_TOOL,
      MARK_DOCUMENT_FIELDS_DIRTY_TOOL,
      MOVE_DOCUMENT_SECTION_TOOL,
      DELETE_DOCUMENT_SECTION_TOOL,
      DELETE_DOCUMENT_BLOCKS_TOOL,
      RENAME_DOCUMENT_HEADING_TOOL,
      INSERT_DOCUMENT_PARAGRAPH_TOOL,
      APPLY_DOCUMENT_OPERATIONS_TOOL,
      READ_DOCUMENT_XML_TOOL,
      EDIT_DOCUMENT_XML_TOOL,
      PATCH_DOCUMENT_XML_TOOL,
      READ_DOCUMENT_IMAGE_TOOL,
      REPACK_DOCUMENT_TOOL,
    ],
    triggers: [],
  },
  presentation: {
    name: 'presentation',
    description: 'Create and edit PowerPoint presentations (.pptx) with slides containing titles, text, bullet points, shapes, tables, charts, images, and slide masters.',
    instructions: presentationSkillMd,
    tools: [
      PRESENTATION_GENERATION_TOOL,
      CAPTURE_SLIDES_TOOL,
      CREATE_THUMBNAIL_TOOL,
      UNPACK_PRESENTATION_TOOL,
      EXTRACT_SLIDE_TEXT_TOOL,
      PATCH_SLIDE_XML_TOOL,
      DUPLICATE_SLIDE_TOOL,
      DUPLICATE_PRESENTATION_TOOL,
      DELETE_SLIDE_TOOL,
      REORDER_SLIDES_TOOL,
      READ_PRESENTATION_IMAGE_TOOL,
      REPACK_PRESENTATION_TOOL,
    ],
    triggers: [
      'powerpoint',
      'presentation',
      '.pptx',
      'pptx',
      'slideshow',
      'pitch deck',
      'slides',
    ],
  },
  pdf: {
    name: 'pdf',
    description: 'Read and extract text/metadata from uploaded PDF files.',
    instructions: pdfSkillMd,
    tools: [PDF_EXTRACT_TEXT_TOOL, PDF_GET_METADATA_TOOL, PDF_OCR_TEXT_TOOL] as unknown as import('@anthropic-ai/sdk/resources/messages').Tool[],
    triggers: [
      'pdf',
      'extract text',
      'read pdf',
      'pdf metadata',
    ],
  },
  spreadsheet: {
    name: 'spreadsheet',
    description: 'Create and edit Excel spreadsheets (.xlsx) with data, formulas, and formatting.',
    instructions: spreadsheetSkillMd,
    tools: [SPREADSHEET_GENERATION_TOOL, EDIT_SPREADSHEET_TOOL, DESCRIBE_SPREADSHEET_TOOL, SUMMARIZE_COLUMN_TOOL, QUERY_CELLS_TOOL, EXTRACT_SPREADSHEET_TEXT_TOOL, RUN_FORMULA_TOOL, FILTER_SPREADSHEET_TOOL],
    triggers: [
      'spreadsheet',
      'excel',
      '.xlsx',
      'xlsx',
      'workbook',
      'financial model',
      'data table',
    ],
  },
};

/**
 * Get a skill by name.
 *
 * @param skillName - The name of the skill to retrieve
 * @returns The skill definition or undefined if not found
 */
export function getSkill(skillName: string): Skill | undefined {
  return skillRegistry[skillName];
}

/**
 * Get skill instructions for the get_skill_instruction tool.
 *
 * @param skillName - The name of the skill to get instructions for
 * @param filename - Optional: specific file to load (default: uses the skill's main instructions)
 * @returns The skill instructions result or null if not found
 */
export function getSkillInstruction(skillName: string, filename?: string): GetSkillInstructionResult | null {
  const skill = skillRegistry[skillName];
  if (!skill) {
    return null;
  }

  // If a specific filename is requested, try to load it from skillFiles
  if (filename) {
    const files = skillFiles[skillName];
    if (files && files[filename]) {
      return {
        skillName: skill.name,
        instructions: files[filename],
        tools: skill.tools,
      };
    }
    // File not found - return null to indicate error
    return null;
  }

  // Default: return the skill's main instructions
  return {
    skillName: skill.name,
    instructions: skill.instructions,
    tools: skill.tools,
  };
}

/**
 * Get all available skill names.
 *
 * @returns Array of skill names
 */
export function getAvailableSkillNames(): string[] {
  return Object.keys(skillRegistry);
}

/**
 * Get summaries of all available skills.
 *
 * @returns Array of skill summaries (without full instructions)
 */
export function getAvailableSkills(): SkillSummary[] {
  return Object.values(skillRegistry).map(skill => ({
    name: skill.name,
    description: skill.description,
    triggers: skill.triggers,
  }));
}

/**
 * Find the most relevant skill based on user input.
 *
 * Checks if any skill's triggers appear in the input text.
 * Returns the first matching skill or null if no match.
 *
 * @param userInput - The user's input text to analyze
 * @returns The matched skill or null
 */
export function findSkillByTrigger(userInput: string): Skill | null {
  const lowerInput = userInput.toLowerCase();

  for (const skill of Object.values(skillRegistry)) {
    if (skill.triggers?.some(trigger => lowerInput.includes(trigger))) {
      return skill;
    }
  }

  return null;
}

/**
 * Get all tools from all skills.
 *
 * Useful when you want to provide all content creation tools at once.
 *
 * @returns Array of all skill tools
 */
export function getAllSkillTools() {
  return Object.values(skillRegistry).flatMap(skill => skill.tools);
}
